import React from "react";
import { Home as HomeIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { SectionToggle, NumInput } from "./shared";

const DEFAULT_RE = {
  enabled: true,
  current_equity: 200000,
  appreciation_rate: 3.0,
  monthly_rental_income: 0,
  include_in_total: true,
  planned_sale_age: null,
};

export default function RealEstateSection({ inputs, setInputs, open, onToggle }) {
  const reOn = inputs.real_estate?.enabled;
  const updateRE = (k, v) => setInputs({ ...inputs, real_estate: { ...inputs.real_estate, [k]: v } });
  const toggleRE = (v) => {
    if (v && !inputs.real_estate) setInputs({ ...inputs, real_estate: { ...DEFAULT_RE } });
    else if (v) updateRE("enabled", true);
    else updateRE("enabled", false);
  };

  return (
    <div>
      <SectionToggle
        testId="toggle-realestate"
        icon={HomeIcon}
        title="Real estate equity"
        subtitle="Home or rental property, optional sale"
        badge={reOn ? "on" : "off"}
        open={open}
        onToggle={onToggle}
      />
      {open && (
        <div className="pb-5 space-y-4">
          <div className="flex items-center justify-between text-sm">
            <Label className="text-[#1C2B23]">Include real estate</Label>
            <Switch data-testid="re-enabled" checked={!!reOn} onCheckedChange={toggleRE} />
          </div>
          {reOn && (
            <>
              <NumInput id="re-equity" testid="re-equity" label="Current equity" value={inputs.real_estate.current_equity} onChange={(v) => updateRE("current_equity", v)} prefix="$" step={5000} />
              <div className="grid grid-cols-2 gap-3">
                <NumInput id="re-app" testid="re-appreciation" label="Appreciation" value={inputs.real_estate.appreciation_rate} onChange={(v) => updateRE("appreciation_rate", v)} suffix="%" step={0.1} />
                <NumInput id="re-rent" testid="re-rental" label="Monthly rental income" value={inputs.real_estate.monthly_rental_income} onChange={(v) => updateRE("monthly_rental_income", v)} prefix="$" step={100} />
              </div>
              <NumInput
                id="re-sale"
                testid="re-sale-age"
                label="Planned sale age (blank = never)"
                value={inputs.real_estate.planned_sale_age ?? ""}
                onChange={(v) => updateRE("planned_sale_age", v === "" ? null : v)}
                suffix="yrs"
              />
              <div className="flex items-center justify-between text-sm">
                <Label className="text-[#5C6B62]">Show RE equity in total balance</Label>
                <Switch
                  data-testid="re-include"
                  checked={inputs.real_estate.include_in_total}
                  onCheckedChange={(v) => updateRE("include_in_total", v)}
                />
              </div>
              <p className="text-[11px] text-[#5C6B62] leading-relaxed">
                RE grows deterministically (no volatility). Rental income offsets withdrawals while you own the property. At sale age, equity moves into your taxable portfolio.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
